import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { reportEvent } from './report-event.helper';
import { LogType } from '../types/logType';

// Funnel steps of one liveness attempt, as seen from this device. Each step is
// reported once per transaction as a LIVENESS_STEP event carrying how long it
// took since the previous step and since the flow started, so Datadog can
// chart step-to-step latency (log-based distribution metrics on
// @stepDurationMs / @msSinceStart grouped by @step and @vendor) and line it up
// with persons-api's own events on @transactionId.
export enum LivenessStep {
    // Desktop or direct mobile: persons-api created the session.
    SESSION_CREATED = 'SESSION_CREATED',
    // Desktop: QR code is on screen.
    QR_DISPLAYED = 'QR_DISPLAYED',
    // Phone opened via the QR link: the existing session loaded.
    QR_OPENED = 'QR_OPENED',
    // A live video stream was opened (either vendor).
    CAMERA_STARTED = 'CAMERA_STARTED',
    // The vendor SDK finished capturing the selfie (Incode: capture success;
    // AWS: onAnalysisComplete, i.e. the video check was uploaded).
    CAPTURE_COMPLETED = 'CAPTURE_COMPLETED',
    // persons-api answered with the verification result (on desktop QR flows
    // this is when polling saw the phone's result).
    RESULT_RECEIVED = 'RESULT_RECEIVED',
}

export type LivenessFlowType = 'mobile' | 'desktop' | 'qr';

interface LivenessFlow {
    transactionId: string;
    vendor?: string;
    personUuid?: string;
    flow: LivenessFlowType;
    startedAt: number;
    lastStep: LivenessStep | null;
    lastStepAt: number;
    reportedSteps: Set<LivenessStep>;
}

let currentFlow: LivenessFlow | null = null;

function now(): number {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
}

// Attach the transaction id to every RUM event and log this page sends from
// here on, including the SDKs' automatic network/error logs, so all of them
// can be joined with persons-api's @transactionId.
function setGlobalTransactionId(transactionId: string): void {
    try {
        datadogRum.setGlobalContextProperty('transactionId', transactionId);
    } catch {
        // Datadog must never break the liveness flow
    }
    try {
        datadogLogs.setGlobalContextProperty('transactionId', transactionId);
    } catch {
        // Datadog must never break the liveness flow
    }
}

/**
 * Begin timing a liveness transaction. Call once the transaction id is known;
 * pass `startedAt` (a performance.now() value) to count from an earlier
 * moment, e.g. when the create-session request was sent. Starting a new
 * transaction (e.g. the Incode → AWS retry) resets the timings.
 */
export function startLivenessFlow(params: {
    transactionId: string;
    vendor?: string;
    personUuid?: string;
    flow: LivenessFlowType;
    startedAt?: number;
}): void {
    if (!params.transactionId) return;
    if (currentFlow?.transactionId === params.transactionId) return;
    const startedAt = params.startedAt ?? now();
    currentFlow = {
        transactionId: params.transactionId,
        vendor: params.vendor,
        personUuid: params.personUuid,
        flow: params.flow,
        startedAt,
        lastStep: null,
        lastStepAt: startedAt,
        reportedSteps: new Set(),
    };
    setGlobalTransactionId(params.transactionId);
}

/**
 * Report a funnel step for the current transaction. Each step is reported at
 * most once per transaction; calls before a flow has started are ignored.
 */
export function markLivenessStep(step: LivenessStep, extra: Record<string, unknown> = {}): void {
    const flow = currentFlow;
    if (!flow || flow.reportedSteps.has(step)) return;
    flow.reportedSteps.add(step);

    const at = now();
    const stepDurationMs = Math.round(at - flow.lastStepAt);
    const msSinceStart = Math.round(at - flow.startedAt);
    const previousStep = flow.lastStep;
    flow.lastStep = step;
    flow.lastStepAt = at;

    reportEvent(`LIVENESS_STEP_${step}`, {
        event: 'LIVENESS_STEP',
        step,
        previousStep,
        stepDurationMs,
        msSinceStart,
        transactionId: flow.transactionId,
        vendor: flow.vendor,
        personUuid: flow.personUuid,
        flow: flow.flow,
        timestamp: new Date().toISOString(),
        ...extra,
    }, LogType.INFO);
}

export function resetLivenessFlowForTests(): void {
    currentFlow = null;
}
